import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Ban, Send, RefreshCw } from "lucide-react";

export default function AutopilotProposals({ onApprove }) {
  const [rows, setRows] = useState([]);
  const [planning, setPlanning] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/autopilot/proposals");
      setRows(r.data || []);
    } catch (e) {
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runPlanner = async () => {
    setPlanning(true);
    try {
      const r = await api.post("/autopilot/plan");
      const proposals = r.data?.proposals || [];
      if (proposals.length === 0) {
        toast.info(r.data?.reason || "Nothing to propose right now.");
      } else {
        toast.success(`${proposals.length} proposal${proposals.length === 1 ? "" : "s"} ready.`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Planner failed.");
    } finally {
      setPlanning(false);
    }
  };

  const dismiss = async (id) => {
    try {
      await api.post(`/autopilot/proposals/${id}/dismiss`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed.");
    }
  };

  return (
    <div className="rounded-2xl border border-emerald-500/20 bg-emerald-950/10 p-6" data-testid="autopilot-proposals-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-400 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Autopilot
          </div>
          <h2 className="font-display font-black text-2xl text-white tracking-tight mt-1">This week&apos;s proposals</h2>
        </div>
        <button
          onClick={runPlanner}
          disabled={planning}
          data-testid="autopilot-plan-btn"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded-md disabled:opacity-40"
        >
          {planning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Run planner
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center">No proposals yet. Tap &quot;Run planner&quot; to compose one.</div>
      ) : (
        <div className="space-y-3" data-testid="autopilot-proposals-list">
          {rows.map((p) => (
            <div key={p.id} data-testid={`proposal-${p.id}`} className="rounded-lg border border-emerald-500/30 bg-zinc-950/60 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white font-medium">{p.campaign_label}</span>
                <span className="text-[11px] text-emerald-300">{p.live_eligible_count} eligible now</span>
              </div>
              <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{p.rationale}</div>
              {p.context && (
                <div className="mt-2 text-[11px] text-zinc-500">
                  <span className="uppercase tracking-[0.18em]">Context:</span> {p.context}
                </div>
              )}
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  onClick={() => dismiss(p.id)}
                  data-testid={`proposal-dismiss-${p.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-zinc-800 text-zinc-400 rounded"
                >
                  <Ban className="w-3 h-3" /> Dismiss
                </button>
                <button
                  onClick={() => onApprove?.(p)}
                  data-testid={`proposal-approve-${p.id}`}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                >
                  <Send className="w-3 h-3" /> Approve & send
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
